/**
 * Svetlý / tmavý režim.
 *
 * Používateľ volí jednu z troch možností (VOLBY): "light", "dark" alebo "auto"
 * — auto sa riadi nastavením zariadenia (prefers-color-scheme). Kto si nič
 * nevybral, má svetlý režim (PREDVOLENA), nie režim zariadenia. Voľba sa
 * pamätá v localStorage pod kľúčom KLUC, spoločne pre všetky prevádzky:
 * je to vlastnosť zariadenia (vodičov telefón vs. počítač v kancelárii), nie
 * prevádzky ani účtu.
 *
 * Výsledný režim ("light" | "dark") sa zapíše do atribútu data-theme na <html>,
 * na ktorý sa viažu farby v styles/theme.scss.
 *
 * POZOR: rovnakú logiku v skratke opakuje inline skript v public/index.html,
 * ktorý beží ešte pred prvým vykreslením (aby tmavá stránka neblikla na bielo).
 * Pri zmene kľúča alebo hodnôt treba upraviť aj ten.
 */

export const KLUC = "vzhlad";
export const VOLBY = ["light", "dark", "auto"];
export const PREDVOLENA = "light";

// Farba lišty prehliadača na mobile (<meta name="theme-color">) — pozadie
// stránky (neutral-100 z styles/_paleta.scss), nech lišta s obsahom splynie.
// Súlad s paletou stráži styles/farby.test.js.
export const FARBA_LISTY = { light: "#FFFFFF", dark: "#0D1117" };

const SYSTEM_TMAVY = "(prefers-color-scheme: dark)";

/** Uložená voľba; čokoľvek neznáme (alebo nedostupné úložisko) je PREDVOLENA. */
export function nacitajVolbu() {
  try {
    const v = window.localStorage.getItem(KLUC);
    return VOLBY.includes(v) ? v : PREDVOLENA;
  } catch {
    return PREDVOLENA;
  }
}

/**
 * Uloží voľbu — každú, aj "auto": prázdne úložisko znamená PREDVOLENA.
 * Ak úložisko nejde (súkromný režim, zablokované údaje), voľba platí aspoň
 * do zatvorenia stránky.
 */
export function ulozVolbu(volba) {
  try {
    window.localStorage.setItem(KLUC, volba);
  } catch {
    // nič — režim sa aj tak prepne, len si ho prehliadač nezapamätá
  }
}

/** Je zariadenie práve v tmavom režime? */
export function systemJeTmavy() {
  try {
    return Boolean(window.matchMedia?.(SYSTEM_TMAVY).matches);
  } catch {
    return false;
  }
}

/** Voľba + stav zariadenia → režim, ktorý sa naozaj zobrazí. */
export function vyhodnot(volba, systemTmavy) {
  if (volba === "dark" || volba === "light") return volba;
  return systemTmavy ? "dark" : "light";
}

/** Zapíše režim do stránky. */
export function aplikuj(rezim) {
  const root = document.documentElement;
  if (root.dataset.theme !== rezim) root.dataset.theme = rezim;

  const meta = document.head.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", FARBA_LISTY[rezim]);
}

/**
 * Sleduje zmenu režimu zariadenia. Vracia funkciu na odhlásenie.
 * Safari < 14 pozná len addListener.
 */
export function sledujSystem(callback) {
  let mql;
  try {
    mql = window.matchMedia?.(SYSTEM_TMAVY);
  } catch {
    mql = null;
  }
  if (!mql) return () => {};

  const handler = (e) => callback(e.matches);
  if (mql.addEventListener) {
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }
  mql.addListener?.(handler);
  return () => mql.removeListener?.(handler);
}
